import React, { useState, useEffect, useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import {
  clientsApi,
  productsApi,
  profileClientApi,
  brandsApi,
  administrativeApi,
} from "@/lib/api";
import { getAccountIdSync } from "@/lib/oidcAuth";
import { initializeSegmentOrder, getSegmentOrder } from "@/lib/segmentApi";
import { Link, useNavigate } from "react-router-dom";
import { useUser } from "@/contexts/UserContext";
import { mapEtapaToSegment, applyProductBonuses } from "@/lib/utils";
import { formatCurrency } from "@/lib/formatters";
import { isProductBonusEnabledForCycle } from "@/features/productBonusFeature";
import { TooltipProvider } from "@/components/ui/tooltip";
import { Card, CardContent, CardHeader } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  ArrowLeft,
  AlertTriangle,
  School as SchoolIcon,
  Eye,
  Package,
} from "lucide-react";

// Reutiliza componentes existentes do OrderInterface
import { Wizad } from "@/components/OrderInterface/wizard";
import ProductTableScheduled from "@/components/OrderInterface/Scheduled-Order/ProductTableScheduled";
import ProfessorStepContent from "@/components/OrderInterface/ProfessorStepContent";
import SimulationSummary2027 from "@/components/OrderInterface/SimulationSummary2027";

// ─── Interfaces ───────────────────────────────────────────────────────────────

interface Client {
  codigo_cnx: string;
  nome_cliente: string;
  shipping_fee: number;
  clientSchoolIdLex?: string;
  cnpj?: string;
  email?: string;
  telefone?: string;
  endereco?: string;
  numero?: string;
  bairro?: string;
  cidade?: string;
  estado?: string;
  cep?: string;
}

interface OrderItem {
  id: number;
  codigo_produto: string;
  descricao_produto: string;
  preco: number;
  ano_letivo: number;
  marca: string;
  sku: string;
  status: string;
  utilizacao?: string | null;
  serie?: string | null;
  segmento?: string | null;
  quantidade_contratual: number;
  desconto: number;
  adjustment: number;
  quantidade_pedido: number;
  ordered_quantity?: number;
  valor_liquido: number;
  valor_total: number;
  delivery_type_id: number;
  delivery_type_number: number;
  brand_id: number;
  product_type: number;
  product_id?: number;
  product_series?: string;
  segment_name?: string;
  brand_name?: string;
  product_school_year?: number;
  product_sku?: string;
  quantity_contracted?: number;
  previousYearProductPrice?: number | null;
  is_printed_product?: boolean;
  related_product_ids?: number[];
  quantity_required_for_bonus?: number | null;
  used_bonus_quantity?: number;
  available_bonus_quantity?: number;
  earned_bonus_quantity?: number;
  bonified_quantity?: number;
  is_optional?: boolean;
}

// ─── Constantes ───────────────────────────────────────────────────────────────

/**
 * Fallback usado só até GET /clients/cycle (via UserContext.viewOnlyCycleYears)
 * responder — nunca usar new Date().getFullYear() aqui.
 */
const DEFAULT_SIMULATION_CYCLE_YEAR = 2027;

// ─── Componente ───────────────────────────────────────────────────────────────

const Simulation2027 = () => {
  const navigate = useNavigate();
  const { selectedClient, openCycleYears, viewOnlyCycleYears } = useUser();
  const simulationCycleYear = viewOnlyCycleYears?.[0] ?? DEFAULT_SIMULATION_CYCLE_YEAR;
  const storageKeyPrefix = `simulation_${simulationCycleYear}_draft_`;
  const [orderItems, setOrderItems] = useState<OrderItem[]>([]);

  useEffect(() => {
    const initializeApp = async () => {
      await initializeSegmentOrder();
    };
    initializeApp();
  }, []);

  const accountId = getAccountIdSync();

  // ── Query: Lista de Escolas / Clientes ──
  const { isLoading: clientsLoading } = useQuery({
    queryKey: ["simulation-schools", accountId],
    queryFn: async () => {
      if (!accountId) return [];
      await administrativeApi.register();
      const { data } = await clientsApi.getSchoolsByAccount(accountId);

      const filteredClients = (data || [])
        .filter((client: any) => client.clientSchoolIdLex !== null)
        .map((client: any) => ({
          codigo_cnx: client.clientCodeCnx,
          nome_cliente: client.clientFantasyName || client.clientCompanyName,
          shipping_fee: Number(client?.shippingFee) || 0,
          clientSchoolIdLex: client.clientSchoolIdLex,
          cnpj: client.clientCnpj,
          email: client.clientEmail,
          telefone: client.clientPhone,
          endereco: client.clientAddress,
          numero: client.clientNumber,
          bairro: client.clientDistrict,
          cidade: client.clientCity,
          estado: client.clientState,
          cep: client.clientPostCode,
        }));

      const map = new Map();
      return filteredClients.filter((item: any) => {
        if (!map.has(item.codigo_cnx)) {
          map.set(item.codigo_cnx, true);
          return true;
        }
        return false;
      });
    },
    enabled: !!accountId,
  });

  // ── Query: Produtos e Perfil do Cliente para o ciclo em simulação ──
  const { data: rawSimulationItems, isLoading: productsLoading } = useQuery<OrderItem[]>({
    queryKey: ["simulation-profile-products", selectedClient?.codigo_cnx, simulationCycleYear],
    queryFn: async () => {
      if (!selectedClient?.codigo_cnx) return [];

      const { data } = await profileClientApi.getByClientCode(
        selectedClient.codigo_cnx,
        simulationCycleYear
      );
      const clientProfileData = data || [];
      if (clientProfileData.length === 0) return [];

      const [productResponse, brandResponse] = await Promise.all([
        productsApi.getAll(simulationCycleYear),
        brandsApi.getAll(),
      ]);

      const productData = productResponse.data || [];
      const brandData = brandResponse.data || [];

      const combinedData = clientProfileData
        .map((clientItem: any) => {
          const productId = clientItem.productId;
          if (!productId) return null;

          const product = productData.find((p) => p.id === productId);
          if (!product) return null;

          const discount = clientItem.discount || 0;
          const adjustment = clientItem.adjustment || 0;
          const contractedQuantity = clientItem.contractualQuantity || 0;
          const brand = brandData.find((b) => b.name === product.marca);
          const price = clientItem?.productPrice || clientItem.preco || 0;
          const unitPriceWithDiscount =
            Math.round(price * (1 - discount / 100) * 100) / 100;

          const quantidadePedido = contractedQuantity;
          const totalValue = Math.round(unitPriceWithDiscount * quantidadePedido * 100) / 100;

          return {
            id: product.id,
            product_id: product.id,
            codigo_produto: product.codigoProduto,
            descricao_produto: product.descricaoProduto,
            preco: price,
            ano_letivo: product.anoLetivo || 0,
            marca: product.marca || "",
            sku: product.sku || "",
            status: product.status || "",
            utilizacao: product.utilizacao,
            serie: product.serie,
            segmento: product.segmento,
            quantidade_contratual: contractedQuantity,
            desconto: discount,
            adjustment: adjustment,
            quantidade_pedido: quantidadePedido,
            ordered_quantity: clientItem.orderedQuantity || 0,
            valor_liquido: unitPriceWithDiscount,
            valor_total: totalValue,
            delivery_type_id: product.deliveryTypeId || 1,
            delivery_type_number: product.deliveryType?.deliveryTypeNumber || 1,
            brand_id: brand?.id || 0,
            product_type: Number(product?.productType) || 1,
            is_printed_product: product.isPrintedProduct,
            previousYearProductPrice: clientItem.previousYearProductPrice ?? null,
            product_series: product.serie,
            segment_name: product.segmento,
            brand_name: product.marca,
            product_school_year: product.anoLetivo,
            product_sku: product.sku,
            quantity_contracted: contractedQuantity,
            related_product_ids: clientItem.relatedProductIds ?? product.relatedProductIds ?? [],
            quantity_required_for_bonus: clientItem.quantityRequiredForBonus ?? null,
            // Valor cru do backend; é sempre recalculado por applyProductBonuses
            // para itens Professor (ver src/lib/utils.ts).
            bonified_quantity: clientItem.bonifiedQuantity ?? 0,
            used_bonus_quantity: clientItem.usedBonusQuantity ?? 0,
            available_bonus_quantity: clientItem.availableBonusQuantity ?? 0,
            is_optional: clientItem.isOptional ?? false,
            is_shipment_exempt: clientItem.isShipmentExempt ?? false,
            shipment_exemption_applies: clientItem.shipmentExemptionApplies ?? false,
            scheduled_shipment_freight_base: clientItem.scheduledShipmentFreightBase ?? null,
          };
        })
        .filter((item): item is NonNullable<typeof item> => item !== null);

      // Ordenar por segmento, série, marca
      const sortedData = combinedData.sort((a, b) => {
        const segmentA = getSegmentOrder(mapEtapaToSegment(a.serie));
        const segmentB = getSegmentOrder(mapEtapaToSegment(b.serie));
        if (segmentA !== segmentB) return segmentA - segmentB;

        const serieComparison = String(a.serie || "").localeCompare(String(b.serie || ""));
        if (serieComparison !== 0) return serieComparison;

        return String(a.marca || "").localeCompare(String(b.marca || ""));
      });

      // Ajustar quantidade_contratual e quantidade_pedido: maior por série
      // (somente itens de Aluno — itens de Professor mantêm a quantidade própria vinda da API)
      const isProfessorItem = (item: OrderItem) =>
        String(item.utilizacao || "").toLowerCase() === "professor";

      const seriesMap = new Map<string, number>();
      sortedData.forEach((item) => {
        if (isProfessorItem(item) || item.is_optional) return;
        const serie = item.serie || "sem_serie";
        const currentMax = seriesMap.get(serie) || 0;
        if (item.quantidade_contratual > currentMax) {
          seriesMap.set(serie, item.quantidade_contratual);
        }
      });

      sortedData.forEach((item) => {
        if (item.is_optional) {
          // Opcional começa em 0 e é editado isoladamente; mantém a própria quantidade contratual
          item.quantidade_pedido = 0;
          item.valor_total = 0;
          return;
        }
        if (isProfessorItem(item)) return;
        const serie = item.serie || "sem_serie";
        const maxQuantityForSeries = seriesMap.get(serie) || item.quantidade_contratual;
        item.quantidade_contratual = maxQuantityForSeries;
        item.quantidade_pedido = maxQuantityForSeries;
        item.valor_total = Math.round(item.valor_liquido * item.quantidade_pedido * 100) / 100;
      });

      return applyProductBonuses(sortedData, isProductBonusEnabledForCycle(simulationCycleYear));
    },
    enabled: !!selectedClient?.codigo_cnx,
  });

  // ── Bonificação de materiais para Professor (compartilhada com OrderInterface/index.tsx via lib/utils) ──
  const applyProductBonusesForOrder = (items: OrderItem[]) =>
    applyProductBonuses(items, isProductBonusEnabledForCycle(simulationCycleYear));

  // ── Sincroniza dados da Query com o estado local e localStorage (rascunho de simulação) ──
  useEffect(() => {
    if (!rawSimulationItems) {
      setOrderItems([]);
      return;
    }

    const itemsCopy: OrderItem[] = rawSimulationItems.map((item) => ({ ...item }));

    if (selectedClient) {
      const storageKey = `${storageKeyPrefix}${selectedClient.codigo_cnx}`;
      const savedDraft = localStorage.getItem(storageKey);
      if (savedDraft) {
        try {
          const draftData = JSON.parse(savedDraft);
          const savedQuantities = draftData?.quantities || {};
          itemsCopy.forEach((item) => {
            if (savedQuantities[item.id] !== undefined) {
              item.quantidade_pedido = savedQuantities[item.id];
              item.valor_total =
                Math.round(item.valor_liquido * item.quantidade_pedido * 100) / 100;
            }
          });
        } catch (e) {
          console.warn("Erro ao restaurar rascunho de simulação 2027:", e);
        }
      }
    }

    setOrderItems(applyProductBonusesForOrder(itemsCopy));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rawSimulationItems, selectedClient?.codigo_cnx, storageKeyPrefix]);

  // ── Atualizar quantidade (estado isolado) ──
  const updateQuantidade = (index: number, quantidade: number) => {
    const newOrderItems = [...orderItems];
    const item = newOrderItems[index];
    if (item) {
      item.quantidade_pedido = Math.max(0, quantidade);
      item.valor_liquido =
        Math.round(item.preco * (1 - item.desconto / 100) * 100) / 100;
      item.valor_total =
        Math.round(item.valor_liquido * item.quantidade_pedido * 100) / 100;

      if (selectedClient) {
        const storageKey = `${storageKeyPrefix}${selectedClient.codigo_cnx}`;
        const existing = JSON.parse(localStorage.getItem(storageKey) || "{}");
        const draftData = {
          quantities: existing?.quantities || {},
          timestamp: new Date().getTime(),
        };
        draftData.quantities[item.id] = item.quantidade_pedido;
        localStorage.setItem(storageKey, JSON.stringify(draftData));
      }

      setOrderItems(applyProductBonusesForOrder(newOrderItems));
    }
  };

  const updateQuantidadesBatch = (updates: Array<{ id: number; quantidade: number }>) => {
    setOrderItems((prevOrderItems) => {
      const newOrderItems = [...prevOrderItems];
      const updatedQuantitiesMap: Record<number, number> = {};

      updates.forEach((update) => {
        const itemIndex = newOrderItems.findIndex((item) => item.id === update.id);
        if (itemIndex !== -1) {
          const item = newOrderItems[itemIndex];
          item.quantidade_pedido = Math.max(0, update.quantidade);
          item.valor_liquido =
            Math.round(item.preco * (1 - item.desconto / 100) * 100) / 100;
          item.valor_total =
            Math.round(item.valor_liquido * item.quantidade_pedido * 100) / 100;
          updatedQuantitiesMap[item.id] = item.quantidade_pedido;
        }
      });

      if (selectedClient) {
        const storageKey = `${storageKeyPrefix}${selectedClient.codigo_cnx}`;
        const existing = JSON.parse(localStorage.getItem(storageKey) || "{}");
        const draftData = {
          quantities: { ...existing?.quantities, ...updatedQuantitiesMap },
          timestamp: new Date().getTime(),
        };
        localStorage.setItem(storageKey, JSON.stringify(draftData));
      }

      return applyProductBonusesForOrder(newOrderItems);
    });
  };

  // ── Segmentos disponíveis ──
  const availableSegments = useMemo(() => {
    return Array.from(
      new Set(orderItems.map((item) => mapEtapaToSegment(item.serie)))
    ).sort((a, b) => {
      const order: Record<string, number> = {
        "Ed. Infantil": 1,
        "Fund. Anos Iniciais": 2,
        "Fund. Anos Finais": 3,
        "Ensino Médio": 4,
        PV: 5,
      };
      return (order[a] || 999) - (order[b] || 999);
    });
  }, [orderItems]);

  // ── Navegação de etapas ──
  const [activeStep, setActiveStep] = useState(0);
  const [completedSteps, setCompletedSteps] = useState<Set<number>>(new Set());

  const totalSteps = availableSegments.length + 2;
  const isInProfessorStep = activeStep === availableSegments.length;
  const isInSummary = activeStep === availableSegments.length + 1;

  const itemIds = useMemo(
    () => orderItems.map((item) => item.id).join(","),
    [orderItems]
  );
  useEffect(() => {
    setActiveStep(0);
    setCompletedSteps(new Set());
  }, [itemIds]);

  const currentSegmentItems = useMemo(() => {
    if (isInSummary || isInProfessorStep) return orderItems;
    const currentSegment = availableSegments[activeStep];
    return orderItems.filter(
      (item) => mapEtapaToSegment(item.serie) === currentSegment
    );
  }, [activeStep, availableSegments, orderItems, isInSummary, isInProfessorStep]);

  const professorItems = useMemo(() => {
    return orderItems.filter(
      (item) => item.utilizacao?.toLowerCase() === "professor"
    );
  }, [orderItems]);

  const goToStep = (stepIndex: number) => {
    if (stepIndex !== activeStep) {
      setCompletedSteps((prev) => new Set([...prev, activeStep]));
    }
    setActiveStep(stepIndex);
  };

  const goToNext = () => {
    if (activeStep < totalSteps - 1) {
      setCompletedSteps((prev) => new Set([...prev, activeStep]));
      setActiveStep(activeStep + 1);
    }
  };

  const goToPrevious = () => {
    if (activeStep > 0) {
      setActiveStep(activeStep - 1);
    }
  };

  const updateSegmentQuantity = (segmentIndex: number, quantidade: number) => {
    const item = currentSegmentItems[segmentIndex];
    const globalIndex = orderItems.findIndex(
      (globalItem) => globalItem.id === item.id
    );
    if (globalIndex !== -1) {
      updateQuantidade(globalIndex, quantidade);
    }
  };

  // ── Render ──
  return (
    <TooltipProvider>
      <div className="min-h-screen bg-background">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 py-4 sm:py-6 lg:py-8 max-w-7xl">
          {/* Tabs de navegação entre Ciclos */}
          <div className="mb-6">
            <Tabs
              value="/customer-portal/realizar-pedido/2027-visualizacao"
              onValueChange={(val) => navigate(val)}
              className="w-full"
            >
              <TabsList className="grid w-full grid-cols-2 max-w-md">
                <TabsTrigger value="/customer-portal/realizar-pedido" className="flex items-center gap-2">
                  <Package className="h-4 w-4" />
                  <span>Pedido Ciclo {openCycleYears?.[0] ?? new Date().getFullYear()}</span>
                </TabsTrigger>
                <TabsTrigger value="/customer-portal/realizar-pedido/2027-visualizacao" className="flex items-center gap-2">
                  <Eye className="h-4 w-4" />
                  <span>Simulação Ciclo {simulationCycleYear}</span>
                </TabsTrigger>
              </TabsList>
            </Tabs>
          </div>

          {/* Link de volta + Banner de aviso */}
          <div className="space-y-4 mb-8">
            <Link
              to="/customer-portal/realizar-pedido"
              className="inline-flex items-center gap-2 text-sm text-primary hover:text-primary/80 transition-colors font-medium"
            >
              <ArrowLeft className="h-4 w-4" />
              Voltar para o Pedido 2026
            </Link>

            {/* <Card className="border-amber-400 bg-amber-50 shadow-md">
              <CardContent className="p-4 flex items-start gap-3">
                <AlertTriangle className="h-6 w-6 text-amber-600 shrink-0 mt-0.5" />
                <div>
                  <h3 className="font-semibold text-amber-900 text-base">
                    Modo Simulação — Ciclo {simulationCycleYear}
                  </h3>
                  <p className="text-sm text-amber-800 mt-1">
                    Este ciclo comercial ainda não está aberto para pedidos.
                    Utilize esta página para simular quantidades e visualizar
                    valores estimados. <strong>Nenhum pedido será gerado.</strong>
                  </p>
                </div>
              </CardContent>
            </Card> */}
          </div>

          {/* Header */}
          <Card className="shadow-card hover:shadow-lg transition-all duration-200 mb-8">
            <CardHeader className="bg-gradient-to-r from-teal-600 to-teal-700 text-white rounded-lg">
              <div className="flex items-center gap-2">
                <Eye className="h-6 w-6" />
                <h2 className="font-semibold tracking-tight flex items-center gap-2 text-xl sm:text-2xl">
                  Simulação do Ciclo {simulationCycleYear}
                </h2>
              </div>
              <div className="text-white/90 text-sm mt-2">
                <p>
                  Preencha as quantidades desejadas para simular o valor total do
                  ciclo {simulationCycleYear}. Os dados preenchidos aqui são
                  salvos localmente e não geram nenhum pedido.
                </p>
              </div>
            </CardHeader>
          </Card>

          {/* Conteúdo */}
          {selectedClient === null && (
            <div className="py-12 text-center border-2 transition-all duration-200 mb-8 rounded-lg">
              <SchoolIcon className="mx-auto h-12 w-12 text-muted-foreground" />
              <h3 className="mt-4 text-lg font-medium">
                Selecione uma unidade escolar no pedido 2026 para visualizar a simulação.
              </h3>
              <Link
                to="/customer-portal/realizar-pedido"
                className="mt-2 inline-block text-primary hover:underline text-sm"
              >
                Ir para o pedido 2026
              </Link>
            </div>
          )}

          {selectedClient && (
            <>
              {productsLoading ? (
                <div className="flex flex-col items-center justify-center py-24">
                  <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary" />
                  <p className="mt-4 text-muted-foreground">
                    Carregando produtos para simulação...
                  </p>
                </div>
              ) : (
                <>
                  {orderItems.length > 0 ? (
                    <div className="space-y-6">
                      {/* Wizard / Stepper */}
                      <Wizad
                        availableSegments={availableSegments}
                        activeStep={activeStep}
                        totalSteps={totalSteps}
                        completedSteps={completedSteps}
                        goToPrevious={goToPrevious}
                        goToNext={goToNext}
                        goToStep={goToStep}
                      />

                      {/* Content Area */}
                      {isInSummary ? (
                        <SimulationSummary2027 orderItems={orderItems} cycleYear={simulationCycleYear} />
                      ) : isInProfessorStep ? (
                        <ProfessorStepContent
                          orderItems={professorItems}
                          fullOrderItems={orderItems}
                          updateQuantidade={updateQuantidade}
                          updateQuantidadesBatch={updateQuantidadesBatch}
                          formatCurrency={formatCurrency}
                          isScheduled={false}
                          isSimulated={false}
                          cycleYear={simulationCycleYear}
                        />
                      ) : (
                        <ProductTableScheduled
                          orderItems={currentSegmentItems}
                          fullOrderItems={orderItems}
                          updateQuantidade={updateSegmentQuantity}
                          updateQuantidadesBatch={updateQuantidadesBatch}
                          formatCurrency={formatCurrency}
                          clientHasOrders={true}
                          isScheduled={false}
                          isSimulated={false}
                          cycleYear={simulationCycleYear}
                        />
                      )}

                      {/* Wizard inferior */}
                      <Wizad
                        availableSegments={availableSegments}
                        activeStep={activeStep}
                        totalSteps={totalSteps}
                        completedSteps={completedSteps}
                        goToPrevious={goToPrevious}
                        goToNext={goToNext}
                        goToStep={goToStep}
                      />
                    </div>
                  ) : (
                    <div className="text-center py-12">
                      <div className="text-6xl mb-4">📦</div>
                      <h3 className="text-xl font-semibold text-muted-foreground mb-2">
                        Nenhum produto encontrado para simulação
                      </h3>
                      <p className="text-sm text-muted-foreground">
                        Os dados do ciclo {simulationCycleYear} podem não estar
                        disponíveis ainda. Verifique com a equipe de operações.
                      </p>
                    </div>
                  )}
                </>
              )}
            </>
          )}
        </div>
      </div>
    </TooltipProvider>
  );
};

export default Simulation2027;
